import { Shield, Users, Settings, CheckCircle, Chrome } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CHROME_WEB_STORE_URL } from "@/config/extension";

const steps = [
  {
    icon: <Shield className="w-8 h-8 text-primary" />,
    title: "Install Chrome Extension",
    description: "Add the Fendly extension to Chrome in one click - no downloads or system changes required.",
    details: [
      "One-click installation from Chrome Web Store",
      "No system restarts or admin privileges needed",
      "Works instantly across all Chrome tabs"
    ]
  },
  {
    icon: <Settings className="w-8 h-8 text-primary" />,
    title: "Configure Your Policies",
    description: "Set up content filtering rules that match your family's needs or organization's requirements.",
    details: [
      "Pre-built policy templates",
      "Custom category selection",
      "Time-based scheduling options"
    ]
  },
  {
    icon: <Users className="w-8 h-8 text-primary" />,
    title: "Protect All Devices",
    description: "Apply your policies across all connected devices automatically with our cross-platform solution.",
    details: [
      "Chrome Extension for immediate protection",
      "DNS filtering for network-wide coverage",
      "Real-time policy enforcement"
    ]
  },
  {
    icon: <CheckCircle className="w-8 h-8 text-primary" />,
    title: "Monitor & Adjust",
    description: "Track usage patterns, review blocked content, and fine-tune your policies as needed.",
    details: [
      "Comprehensive analytics dashboard",
      "Real-time activity monitoring",
      "Easy policy adjustments"
    ]
  }
];

export default function HowItWorksSection() {
  return (
    <section className="py-16 md:py-32" id="how-it-works">
      <div className="mx-auto max-w-6xl px-6">
        <div className="text-center mb-16">
          <h2 className="text-3xl font-semibold mb-4">How Fendly Works</h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Get started with Fendly in just a few minutes. Our simple setup process 
            makes it easy to protect your entire family or organization.
          </p>
        </div>
        
        <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-4">
          {steps.map((step, index) => (
            <Card key={index} className="text-center p-6">
              <CardHeader className="pb-4">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  {step.icon}
                </div>
                <div className="flex items-center justify-center mb-2">
                  <span className="w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-bold">
                    {index + 1}
                  </span>
                </div>
                <CardTitle className="text-xl mb-2">{step.title}</CardTitle>
                <p className="text-muted-foreground text-sm">{step.description}</p>
              </CardHeader>
              
              <CardContent className="text-left">
                <ul className="space-y-2">
                  {step.details.map((detail, detailIndex) => (
                    <li key={detailIndex} className="flex items-start gap-2 text-sm">
                      <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                      <span>{detail}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          ))}
        </div>
        
        <div className="text-center mt-12">
          <p className="text-muted-foreground mb-6">
            Ready to get started? It only takes a few minutes to set up your first policy.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              asChild 
              size="lg" 
              className="bg-gradient-to-r from-[#00F5A0] to-[#0AEFFF] text-[#0A0F0D] font-semibold"
            >
              <a 
                href={CHROME_WEB_STORE_URL} 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-2"
              >
                <Chrome className="w-5 h-5" />
                Install Extension
              </a>
            </Button>
            <a 
              href="/auth" 
              className="inline-flex items-center justify-center px-6 py-3 border border-border rounded-lg font-medium hover:bg-muted transition-colors"
            >
              Start Free Trial
            </a>
            <a 
              href="/dashboard" 
              className="inline-flex items-center justify-center px-6 py-3 border border-border rounded-lg font-medium hover:bg-muted transition-colors"
            >
              View Demo
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
